/** Marketing endpoints (`/api/v1/marketing/*`), typed with the hand-written shapes of marketing_types.ts. */
import type { ApiClient, QueryParameterValue } from './http_client';
import type {
  AdRow,
  AutomationDraft,
  AutomationReport,
  ConnectionStart,
  ContactList,
  ContactListQuery,
  ContentItem,
  PosStatus,
  Conversation,
  MarketingCampaign,
  MarketingCampaignDraft,
  MarketingConnection,
  MarketingOverview,
  MarketingRange,
  SalesImportResult,
  SalesImportRow,
  SalesReport,
} from './marketing_types';

function rangeQuery(range: MarketingRange): Record<string, QueryParameterValue> {
  return { from: range.from, to: range.to };
}

export function createMarketingEndpoints(apiClient: ApiClient) {
  return {
    overview: (range: MarketingRange, signal?: AbortSignal) =>
      apiClient.get<MarketingOverview>('/marketing/overview', { signal, query: rangeQuery(range) }),
    campaigns: (signal?: AbortSignal) =>
      apiClient.get<MarketingCampaign[]>('/marketing/campaigns', { signal }),
    createCampaign: (draft: MarketingCampaignDraft) =>
      apiClient.post<MarketingCampaign>('/marketing/campaigns', draft),
    updateCampaign: (campaignId: string, draft: MarketingCampaignDraft) =>
      apiClient.put<MarketingCampaign>(`/marketing/campaigns/${encodeURIComponent(campaignId)}`, draft),
    deleteCampaign: (campaignId: string) =>
      apiClient.delete<{ ok: boolean }>(`/marketing/campaigns/${encodeURIComponent(campaignId)}`),
    content: (signal?: AbortSignal) => apiClient.get<ContentItem[]>('/marketing/content', { signal }),
    ads: (range: MarketingRange, signal?: AbortSignal) =>
      apiClient.get<AdRow[]>('/marketing/ads', { signal, query: rangeQuery(range) }),
    automation: (range: MarketingRange, signal?: AbortSignal) =>
      apiClient.get<AutomationReport>('/marketing/automation', { signal, query: rangeQuery(range) }),
    contacts: (listQuery: ContactListQuery, signal?: AbortSignal) =>
      apiClient.get<ContactList>('/marketing/contacts', {
        signal,
        query: {
          search: listQuery.search,
          kind: listQuery.kind,
          limit: listQuery.limit,
          offset: listQuery.offset,
        },
      }),
    conversation: (contactId: string, signal?: AbortSignal) =>
      apiClient.get<Conversation>(`/marketing/contacts/${encodeURIComponent(contactId)}/messages`, {
        signal,
      }),
    sendReply: (contactId: string, text: string) =>
      apiClient.post<{ ok: boolean }>(`/marketing/contacts/${encodeURIComponent(contactId)}/messages`, {
        text,
      }),
    saveContactTags: (contactId: string, tags: readonly string[]) =>
      apiClient.put<{ ok: boolean }>(`/marketing/contacts/${encodeURIComponent(contactId)}/tags`, { tags }),
    createAutomation: (draft: AutomationDraft) => apiClient.post<unknown>('/marketing/automations', draft),
    updateAutomation: (automationId: string, draft: AutomationDraft) =>
      apiClient.put<unknown>(`/marketing/automations/${encodeURIComponent(automationId)}`, draft),
    switchAutomation: (automationId: string, isActive: boolean) =>
      apiClient.patch<unknown>(`/marketing/automations/${encodeURIComponent(automationId)}`, {
        is_active: isActive,
      }),
    deleteAutomation: (automationId: string) =>
      apiClient.delete<{ ok: boolean }>(`/marketing/automations/${encodeURIComponent(automationId)}`),
    sales: (range: MarketingRange, signal?: AbortSignal) =>
      apiClient.get<SalesReport>('/marketing/sales', { signal, query: rangeQuery(range) }),
    importSales: (rows: readonly SalesImportRow[]) =>
      apiClient.post<SalesImportResult>('/marketing/sales/import', { rows }),
    posStatus: (signal?: AbortSignal) => apiClient.get<PosStatus>('/marketing/pos', { signal }),
    connectBillz: (secretToken: string) =>
      apiClient.post<{ shops: PosStatus['shops'] }>('/marketing/pos/billz', { secret_token: secretToken }),
    saveBillzStores: (storeMap: Readonly<Record<string, string>>) =>
      apiClient.put<{ ok: boolean }>('/marketing/pos/billz/stores', { store_map: storeMap }),
    disconnectBillz: () => apiClient.delete<{ ok: boolean }>('/marketing/pos/billz'),
    saveLocalCities: (cities: readonly string[]) =>
      apiClient.put<{ ok: boolean }>('/marketing/settings/local-cities', { cities }),
    saveMargin: (marginPercent: number) =>
      apiClient.put<{ ok: boolean }>('/marketing/settings', { margin_percent: marginPercent }),
    connections: (signal?: AbortSignal) =>
      apiClient.get<MarketingConnection[]>('/marketing/connections', { signal }),
    startInstagramConnection: () =>
      apiClient.post<ConnectionStart>('/marketing/connections/instagram/start', {}),
    disconnectInstagramAccount: (accountId: string) =>
      apiClient.delete<{ ok: boolean }>(`/marketing/connections/instagram/${encodeURIComponent(accountId)}`),
    eraseInstagramAccount: (accountId: string, confirmName: string) =>
      apiClient.post<{ ok: boolean }>(`/marketing/connections/instagram/${encodeURIComponent(accountId)}/erase`, {
        confirm_name: confirmName,
      }),
    deleteContactData: (contactId: string) =>
      apiClient.delete<{ ok: boolean }>(`/marketing/contacts/${encodeURIComponent(contactId)}`),
  };
}
